/**
 * Strook boven de actiebalk, één component voor alle panes (pane-schil fase 1,
 * 21 september 2026). Drie standen:
 *
 *  - bezig:   fase, dunne voortgangsbalk met "n van m" (of onbepaald), en een Stop-knop;
 *  - melding: laatste status of fout, met sluitkruisje en een detailregel voor ondersteuning;
 *  - leeg:    niets (de aanroeper geeft dan `null` aan de ActionBar).
 *
 * Vervangt de eigen stroken van ToD en SmartTie, de ontbrekende bezig-stand van Sampling en
 * de toast van Snap. Voor iets wat blijft (hervatten, scope, ontbrekende gegevens) is de
 * StatusBand boven de inhoud de plek; de strook is voor wat voorbijgaat.
 */

import * as React from "react";
import { shellText } from "./shellText";
import { useLang } from "../../i18n/LangContext";
import "./pane.css";

export interface PaneStripBusy {
  /** Wat er gebeurt, kort: "Regels beoordelen". */
  phase: string;
  /** Voortgang; zonder `total` is de balk onbepaald. */
  done?: number;
  total?: number;
  /** Stoppen na de lopende stap. */
  onStop?: () => void;
  stopTitle?: string;
}

export interface PaneStripMessage {
  tone: "info" | "ok" | "error" | "neutral";
  text: React.ReactNode;
  /** Technische details, ingeklapt achter "Details voor ondersteuning". */
  detail?: string;
  onClose?: () => void;
}

export interface PaneStripProps {
  busy?: PaneStripBusy | null;
  message?: PaneStripMessage | null;
  className?: string;
}

/** Strook of `null` als er niets te tonen is; geef het resultaat aan `ActionBar strip=`. */
export function PaneStrip({ busy, message, className }: PaneStripProps): JSX.Element | null {
  const lang = useLang();
  if (busy) {
    const determinate = typeof busy.total === "number" && busy.total > 0;
    const pct = determinate
      ? Math.max(0, Math.min(100, Math.round(((busy.done ?? 0) / (busy.total as number)) * 100)))
      : 0;
    return (
      <div
        className={`pane-strip pane-strip--busy${className ? ` ${className}` : ""}`}
        role="status"
        aria-live="polite"
      >
        <div className="pane-strip__text">
          <div className="pane-strip__line">
            <strong>{busy.phase}</strong>
            {determinate ? (
              <span className="pane-strip__count">
                {busy.done ?? 0} {shellText("of", lang)} {busy.total}
              </span>
            ) : null}
          </div>
          <div
            className={`pane-progressbar${determinate ? "" : " is-indeterminate"}`}
            aria-hidden="true"
          >
            <div
              className="pane-progressbar__fill"
              style={determinate ? { width: `${pct}%` } : undefined}
            />
          </div>
        </div>
        {busy.onStop ? (
          <button
            type="button"
            className="pane-btn pane-strip__btn"
            onClick={busy.onStop}
            title={busy.stopTitle ?? shellText("stopTitle", lang)}
          >
            {shellText("stop", lang)}
          </button>
        ) : null}
      </div>
    );
  }
  if (message) {
    const tone = message.tone === "neutral" ? "" : ` pane-strip--${message.tone}`;
    return (
      <div
        className={`pane-strip${tone}${className ? ` ${className}` : ""}`}
        role={message.tone === "error" ? "alert" : "status"}
      >
        <span className="pane-strip__text">
          {message.text}
          {message.detail ? (
            <details className="pane-error-detail">
              <summary>{shellText("supportDetails", lang)}</summary>
              <code>{message.detail}</code>
            </details>
          ) : null}
        </span>
        {message.onClose ? (
          <button
            type="button"
            className="pane-strip__close"
            onClick={message.onClose}
            title={shellText("closeMessage", lang)}
            aria-label={shellText("closeMessage", lang)}
          >
            ×
          </button>
        ) : null}
      </div>
    );
  }
  return null;
}
